import { HqlCode } from '@/components/hql/HqlCode';
import { WhenToApplyQuote } from '@/components/hql/WhenToApplyQuote';
import { MetadataItem } from '@/components/MetadataItem';
import { UserChip } from '@/components/UserChip';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAppliesToActions, useIsDraftPending } from '@/db/useAppliesToActions';
import { hqlCatalogState, taskTypesForConsiderationTypeState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDateTime } from '@/util/dates';
import { checkHql, type HqlCatalog } from '@rock/hql';
import type { AppliesToDraft, AppliesToOwner, ConsiderationType } from '@rock/types';
import { appliesToOwnerCopy, whenToApplyText } from '@rock/types';
import { considerationCadence } from '@rock/util';
import { type Approval, approvalSettlesDraft, redraftBlocker } from '../appliesToStatus';
import { CircleCheck, TriangleAlert } from 'lucide-react';
import type { FC, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { QueryDiagnostics, QueryInput } from '@/components/hql/QueryInput';
import { QueryReferences } from './QueryReferences';

type Editing = { initial: string } & Omit<Approval, 'query'>;

/**
 * A consideration or lesson type's applies-to query: the When To Apply / When To Show text it comes
 * from, the approved query (the only one anything evaluates), the AI draft waiting beside it, and the
 * editor that approves either.
 */
export const AppliesToCard: FC<{ type: AppliesToOwner }> = ({ type }) => {
    const [catalog, catalogLoadable] = useLoadableAtomValue(hqlCatalogState);
    const [editing, setEditing] = useState<Editing>();
    const actions = useAppliesToActions();
    const draft = type.appliesToDraft;

    async function approve(approval: Approval) {
        const clearDraft = !!catalog && approvalSettlesDraft(type, approval, catalog);
        if (await actions.approve(type, approval.query, { clearDraft })) setEditing(undefined);
    }

    return (
        <Card>
            <CardHeader className="border-b-1 pb-3">
                <CardTitle className="text-xl">Applies To</CardTitle>
                <div className="section-description">
                    {type.object === 'lesson-type' ? (
                        <>
                            Which homes are shown this lesson. Only the approved query is evaluated;{' '}
                            <HqlCode query="home" /> shows it to every home that has set up its space.
                        </>
                    ) : (
                        'Which homes get this consideration, and which of their equipment it covers. Only the approved query is evaluated.'
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-5">
                {type.object === 'consideration-type' ? <CadenceBanner considerationType={type} /> : null}
                <MetadataItem
                    label={appliesToOwnerCopy(type).textLabel}
                    value={<WhenToApplyQuote text={whenToApplyText(type)} />}
                />
                {!catalog ? (
                    <div className="text-muted-foreground">
                        {catalogLoadable.state === 'hasError' ? 'Could not load the catalog.' : 'Loading…'}
                    </div>
                ) : editing ? (
                    <>
                        {draft && draft.draftedAt !== editing.seen?.draftedAt ? (
                            <div className="text-sm text-amber-800">
                                A newer draft arrived while you were editing. It stays for review unless it asks the
                                same as the query you approve.
                            </div>
                        ) : null}
                        <QueryEditor
                            key={editing.initial}
                            initial={editing.initial}
                            catalog={catalog}
                            type={type}
                            busy={actions.busy}
                            onApprove={(query) => approve({ query, fromDraft: editing.fromDraft, seen: editing.seen })}
                            onCancel={() => setEditing(undefined)}
                        />
                    </>
                ) : (
                    <ApprovedQuery
                        type={type}
                        catalog={catalog}
                        busy={actions.busy}
                        onEdit={() => setEditing({ initial: type.appliesTo ?? '', fromDraft: false, seen: draft })}
                        onRedraft={() => actions.redraft(type)}
                    />
                )}
                {draft && catalog && !editing ? (
                    <DraftPanel
                        draft={draft}
                        type={type}
                        catalog={catalog}
                        busy={actions.busy}
                        onApprove={(query) => approve({ query, fromDraft: true, seen: draft })}
                        onEdit={() => setEditing({ initial: draft.query ?? '', fromDraft: true, seen: draft })}
                        onDismiss={() => actions.dismissDraft(type)}
                        onRedraft={() => actions.redraft(type)}
                    />
                ) : null}
            </CardContent>
        </Card>
    );
};

/** A consideration with both one-time and recurring tasks can't be scheduled, so no home gets it. */
const CadenceBanner: FC<{ considerationType: ConsiderationType }> = ({ considerationType }) => {
    const [taskTypes] = useLoadableAtomValue(
        useMemo(() => taskTypesForConsiderationTypeState(considerationType.id), [considerationType.id]),
        [],
    );
    if (considerationCadence(taskTypes) !== 'mixed') return null;
    return (
        <Banner>
            Mixed task cadence: this consideration has both one-time and recurring tasks, so no home gets it until its
            tasks are split.
        </Banner>
    );
};

const ApprovedQuery: FC<{
    type: AppliesToOwner;
    catalog: HqlCatalog;
    busy: boolean;
    onEdit: () => void;
    onRedraft: () => void;
}> = ({ type, catalog, busy, onEdit, onRedraft }) => {
    const query = type.appliesTo;
    const result = useMemo(
        () => (query ? checkHql(query, catalog, { spaceId: type.spaceId }) : undefined),
        [query, catalog, type.spaceId],
    );
    return (
        <div className="space-y-3">
            <MetadataItem
                label="Approved Query"
                value={query ? <HqlCode query={query} block /> : <NoQuery type={type} />}
            />
            {query && result ? (
                <>
                    {result.compiled ? (
                        <div className="flex items-center gap-1.5 text-sm text-green-700">
                            <CircleCheck className="h-4 w-4" />
                            Valid against the current catalog.
                        </div>
                    ) : (
                        <Banner>
                            {type.object === 'lesson-type'
                                ? 'This query no longer validates, so this lesson is frozen: it reaches no new homes, and nothing already given is removed.'
                                : 'This query no longer validates, so this consideration is frozen: no new suggestions, and nothing already suggested is removed.'}
                        </Banner>
                    )}
                    <QueryDiagnostics text={query} result={result} />
                    <QueryReferences result={result} catalog={catalog} />
                    <ApprovedBy type={type} />
                </>
            ) : null}
            <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={busy} onClick={onEdit}>
                    {query ? 'Edit query' : 'Write a query'}
                </Button>
                {type.appliesToDraft ? null : <RedraftButton type={type} busy={busy} onRedraft={onRedraft} />}
            </div>
        </div>
    );
};

const NoQuery: FC<{ type: AppliesToOwner }> = ({ type }) =>
    type.object === 'consideration-type' ? (
        <Banner>No approved query, so no home gets this consideration.</Banner>
    ) : (
        <Banner>No approved query, so no home is shown this lesson until one is approved.</Banner>
    );

const ApprovedBy: FC<{ type: AppliesToOwner }> = ({ type }) => {
    if (!type.appliesToApprovedAt) return null;
    return (
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>Approved {formatDateTime(type.appliesToApprovedAt)}</span>
            {type.appliesToApprovedBy ? (
                <>
                    <span>by</span>
                    <UserChip userId={type.appliesToApprovedBy} size="sm" link />
                </>
            ) : null}
        </div>
    );
};

/** Drafts a query from the current When To Apply / When To Show text. */
const RedraftButton: FC<{
    type: AppliesToOwner;
    busy: boolean;
    onRedraft: () => void;
}> = ({ type, busy, onRedraft }) => {
    const blocker = redraftBlocker(type);
    const pending = useIsDraftPending()(type);
    return (
        <>
            <Button variant="ghost" size="sm" disabled={busy || pending || !!blocker} onClick={onRedraft}>
                {pending ? 'Processing…' : `Process "${appliesToOwnerCopy(type).textLabel}" Again`}
            </Button>
            {blocker ? <span className="text-xs text-muted-foreground">{blocker}</span> : null}
        </>
    );
};

const DraftPanel: FC<{
    draft: AppliesToDraft;
    type: AppliesToOwner;
    catalog: HqlCatalog;
    busy: boolean;
    onApprove: (query: string) => void;
    onEdit: () => void;
    onDismiss: () => void;
    onRedraft: () => void;
}> = ({ draft, type, catalog, busy, onApprove, onEdit, onDismiss, onRedraft }) => {
    const result = useMemo(
        () => (draft.query ? checkHql(draft.query, catalog, { spaceId: type.spaceId }) : undefined),
        [draft.query, catalog, type.spaceId],
    );
    const textChanged = draft.whenToApply.trim() !== whenToApplyText(type)?.trim();
    return (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50/60 p-4">
            <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-amber-900">Draft awaiting review</div>
                <div className="text-xs text-muted-foreground">Drafted {formatDateTime(draft.draftedAt)}</div>
            </div>
            {textChanged ? (
                <div className="space-y-1">
                    <div className="text-sm text-amber-800">
                        Drafted from earlier {appliesToOwnerCopy(type).textLabel} text. Use Process "
                        {appliesToOwnerCopy(type).textLabel}" Again to catch up.
                    </div>
                    <WhenToApplyQuote text={draft.whenToApply} />
                </div>
            ) : null}
            <MetadataItem
                label="Drafted Query"
                value={
                    draft.query ? (
                        <HqlCode query={draft.query} block />
                    ) : (
                        <div className="text-sm">No query drafted.</div>
                    )
                }
            />
            {draft.issue ? <div className="whitespace-pre-wrap text-sm text-amber-800">{draft.issue}</div> : null}
            {draft.query && result ? <QueryDiagnostics text={draft.query} result={result} /> : null}
            <div className="flex flex-wrap gap-2">
                <Button
                    size="sm"
                    disabled={busy || !result?.compiled}
                    onClick={() => result?.compiled && onApprove(result.compiled.text)}
                >
                    Approve
                </Button>
                <Button variant="outline" size="sm" disabled={busy} onClick={onEdit}>
                    Edit
                </Button>
                <Button variant="ghost" size="sm" disabled={busy} onClick={onDismiss}>
                    Dismiss
                </Button>
                <RedraftButton type={type} busy={busy} onRedraft={onRedraft} />
            </div>
        </div>
    );
};

const QueryEditor: FC<{
    initial: string;
    catalog: HqlCatalog;
    type: AppliesToOwner;
    busy: boolean;
    onApprove: (query: string) => void;
    onCancel: () => void;
}> = ({ initial, catalog, type, busy, onApprove, onCancel }) => {
    const [text, setText] = useState(initial);
    const result = useMemo(() => checkHql(text, catalog, { spaceId: type.spaceId }), [text, catalog, type.spaceId]);
    const compiled = result.compiled;
    return (
        <div className="space-y-3">
            <QueryInput value={text} onChange={setText} result={result} autoFocus placeholder="e.g. mini-split" />
            {compiled ? (
                <div className="text-sm text-muted-foreground">
                    Approves as <HqlCode query={compiled.text} />
                </div>
            ) : null}
            <div className="flex gap-2">
                <Button size="sm" disabled={busy || !compiled} onClick={() => compiled && onApprove(compiled.text)}>
                    Approve
                </Button>
                <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </div>
    );
};

const Banner: FC<{ children: ReactNode }> = ({ children }) => (
    <div className="flex items-center gap-3 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
        <TriangleAlert className="h-4 w-4 shrink-0 text-destructive" />
        <div>{children}</div>
    </div>
);
